import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { format, startOfWeek, addDays, addWeeks, subWeeks } from "date-fns";
import "./DriverSchedule.css";
import { useAdmin } from "../context/AdminContext";
import { ROLE_LABELS, ROLES } from "../constants/rbac";
import {
  isVehicleReadyForAssignment,
  getVehicleEffectiveStatus,
  formatStatusLabel,
} from "../utils/helpers";
import Select from "../components/SafeSelect";
import DriverDetailsModal from "../components/DriverDetailsModal";
import StaffDetailsModal from "../components/StaffDetailsModal";

const DriverSchedule = ({
  defaultScope = "drivers",
  hideScopeToggle = true,
  myScheduleOnly = false,
  titleOverride = "",
  readOnly = false,
}) => {
  const {
    user,
    drivers,
    vehicles,
    tasks,
    dashboardUsers,
    updateDriver,
    updateVehicle,
    updateDashboardUser,
  } = useAdmin();
  const [searchTerm, setSearchTerm] = useState("");
  const [scheduleScope, setScheduleScope] = useState(defaultScope);
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedDriverForDetails, setSelectedDriverForDetails] = useState(null);
  const [openDriverDetailsModal, setOpenDriverDetailsModal] = useState(false);
  const [selectedStaffForDetails, setSelectedStaffForDetails] = useState(null);
  const [openStaffDetailsModal, setOpenStaffDetailsModal] = useState(false);
  const [assigningVehicleDriverId, setAssigningVehicleDriverId] = useState("");
  const [selectedEntityType, setSelectedEntityType] = useState("drivers");
  const [selectedAvailabilityIndex, setSelectedAvailabilityIndex] =
    useState(null);
  const [eventForm, setEventForm] = useState({
    isAvailable: true,
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    isAllDay: false,
    notes: "",
  });

  React.useEffect(() => {
    const nextScope =
      defaultScope === "staff" || myScheduleOnly ? "staff" : "drivers";
    setScheduleScope(nextScope);
    setSelectedDriver(null);
    setSelectedEntityType(nextScope);
    setOpenDialog(false);
  }, [defaultScope, myScheduleOnly]);

  // Generate week days
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  const staffUsers = useMemo(() => {
    if (myScheduleOnly) {
      if (!user?.uid) {
        return [];
      }

      return [
        {
          id: user.uid,
          uid: user.uid,
          name: user.name || user.displayName || user.email || "Staff User",
          email: user.email || "",
          role: user.role,
          photoURL: user.photoURL || null,
          availabilitySchedule: Array.isArray(user.availabilitySchedule)
            ? user.availabilitySchedule
            : [],
        },
      ];
    }

    if (!dashboardUsers) return [];
    const allStaffUsers = dashboardUsers.filter(
      (dashboardUser) =>
        dashboardUser.role === ROLES.MANAGER ||
        dashboardUser.role === ROLES.DISPATCH
    );

    return allStaffUsers;
  }, [
    dashboardUsers,
    myScheduleOnly,
    user?.uid,
    user?.name,
    user?.displayName,
    user?.email,
    user?.role,
    user?.photoURL,
    user?.availabilitySchedule,
  ]);

  const activeEntities = useMemo(() => {
    return scheduleScope === "staff" ? staffUsers : drivers;
  }, [scheduleScope, staffUsers, drivers]);

  const isAdminUser = user?.role === ROLES.ADMIN;
  const canAssignFleetVehicle =
    scheduleScope === "drivers" && isAdminUser && !readOnly;

  const getEntityTypeLabel = (entityType = scheduleScope) => {
    return entityType === "staff" ? "Staff" : "Driver";
  };

  const getEntitySubtitle = (entity, entityType = scheduleScope) => {
    if (!entity) return "";

    if (entityType === "staff") {
      const roleLabel = entity.role
        ? ROLE_LABELS[entity.role] || entity.role
        : "Staff";
      return entity.email ? `${roleLabel} • ${entity.email}` : roleLabel;
    }

    return entity.driverId || entity.phone || entity.id;
  };

  const getDriverIdentifiers = (driver = null) => {
    if (!driver) {
      return [];
    }

    return Array.from(
      new Set([driver.userId, driver.uid, driver.id].filter(Boolean))
    );
  };

  const getDriverIdentifierSet = (driver = null) =>
    new Set(
      getDriverIdentifiers(driver).map((identifier) => String(identifier).trim())
    );

  const getVehicleDisplayName = (vehicleData = null) => {
    if (!vehicleData) {
      return "";
    }

    const type = vehicleData.vehicleType || vehicleData.type || "";
    const model = vehicleData.model || "";
    const plate = vehicleData.licensePlate || vehicleData.plateNumber || "";

    const baseName = `${type} ${model}`.trim();
    if (!baseName && !plate) {
      return "";
    }

    return plate ? `${baseName} (${plate})`.trim() : baseName;
  };

  const getOwnVehicleLabel = (driver = null) =>
    getVehicleDisplayName(driver?.ownVehicle || null);

  const getAssignedFleetVehicleForDriver = (driver = null) => {
    if (!driver) {
      return null;
    }

    const assignedVehicleId =
      driver?.assignedVehicle?.id || driver?.assignedVehicleId;

    if (assignedVehicleId) {
      const vehicleById = vehicles.find(
        (vehicle) => vehicle.id === assignedVehicleId
      );

      if (vehicleById) {
        return vehicleById;
      }
    }

    const identifiers = getDriverIdentifierSet(driver);
    const vehicleByAssignedDriver = vehicles.find((vehicle) => {
      const assignedDriver = String(vehicle?.assignedDriver || "").trim();
      return assignedDriver && identifiers.has(assignedDriver);
    });

    return vehicleByAssignedDriver || driver.assignedVehicle || null;
  };

  const getFleetOptionsForDriver = (driver = null) => {
    const driverIdentifiers = getDriverIdentifierSet(driver);
    const currentAssignedVehicleId =
      getAssignedFleetVehicleForDriver(driver)?.id ||
      driver?.assignedVehicle?.id ||
      driver?.assignedVehicleId ||
      "";

    return (vehicles || []).filter((vehicle) => {
      if (!vehicle?.id) {
        return false;
      }

      if (vehicle.id === currentAssignedVehicleId) {
        return true;
      }

      const status = String(vehicle.status || "")
        .trim()
        .toLowerCase();
      const assignedDriver = String(vehicle.assignedDriver || "").trim();

      if (assignedDriver && driverIdentifiers.has(assignedDriver)) {
        return true;
      }

      if (assignedDriver && !driverIdentifiers.has(assignedDriver)) {
        return isVehicleReadyForAssignment(vehicle, tasks, { lockHours: 4 });
      }

      if (status === "available" || !status) {
        return true;
      }

      return isVehicleReadyForAssignment(vehicle, tasks, { lockHours: 4 });
    });
  };

  const filteredEntities = useMemo(() => {
    if (!activeEntities) return [];

    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return activeEntities;

    return activeEntities.filter((entity) => {
      const fields = [
        entity.name,
        scheduleScope === "staff" ? entity.email : entity.driverId,
        scheduleScope === "staff" ? entity.role : entity.phone,
      ];

      return fields.some((fieldValue) =>
        String(fieldValue || "")
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });
  }, [activeEntities, scheduleScope, searchTerm]);

  const saveAvailabilityForEntity = async (
    entityType,
    entityId,
    updatedSchedule
  ) => {
    if (entityType === "staff") {
      return await updateDashboardUser(entityId, {
        availabilitySchedule: updatedSchedule,
      });
    }

    return await updateDriver(entityId, {
      availabilitySchedule: updatedSchedule,
    });
  };

  // Get availability for a specific entity and date
  const getEntityAvailability = (entity, date) => {
    if (
      !entity.availabilitySchedule ||
      !Array.isArray(entity.availabilitySchedule)
    ) {
      return [];
    }

    // Filter availability entries that fall on this date
    return entity.availabilitySchedule
      .filter((avail) => {
        const startDate = avail.startDate?.toDate
          ? avail.startDate.toDate()
          : new Date(avail.startDate);
        const endDate = avail.endDate?.toDate
          ? avail.endDate.toDate()
          : new Date(avail.endDate);

        // Check if the date falls within the availability range
        const dateOnly = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        );
        const startOnly = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate()
        );
        const endOnly = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate()
        );

        return dateOnly >= startOnly && dateOnly <= endOnly;
      })
      .map((avail) => ({
        ...avail,
        status: avail.isAvailable ? "available" : "unavailable",
      }));
  };

  const handlePreviousWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  };

  const handleToday = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  const handleOpenEntityDetails = (entity) => {
    if (scheduleScope === "staff") {
      setSelectedStaffForDetails(entity);
      setOpenStaffDetailsModal(true);
      return;
    }

    setSelectedDriverForDetails(entity);
    setOpenDriverDetailsModal(true);
  };

  const handleCloseDriverDetails = () => {
    setOpenDriverDetailsModal(false);
    setSelectedDriverForDetails(null);
  };

  const handleCloseStaffDetails = () => {
    setOpenStaffDetailsModal(false);
    setSelectedStaffForDetails(null);
  };

  const handleAssignFleetVehicleToDriver = async (driver, vehicleId) => {
    if (!driver || !isAdminUser) {
      return;
    }

    const normalizedVehicleId = String(vehicleId || "").trim();
    const currentAssignedVehicleId = String(
      getAssignedFleetVehicleForDriver(driver)?.id ||
        driver?.assignedVehicle?.id ||
        driver?.assignedVehicleId ||
        ""
    ).trim();

    if (normalizedVehicleId === currentAssignedVehicleId) {
      return;
    }

    setAssigningVehicleDriverId(driver.id);

    try {
      const selectedVehicle = normalizedVehicleId
        ? vehicles.find((vehicle) => vehicle.id === normalizedVehicleId)
        : null;

      if (normalizedVehicleId && !selectedVehicle) {
        window.alert("Selected fleet vehicle was not found.");
        return;
      }

      const driverIdentifiers = getDriverIdentifiers(driver);
      const driverPrimaryIdentifier =
        driver.userId || driver.uid || driver.id || "";

      if (selectedVehicle) {
        const selectedVehicleAssignedDriver = String(
          selectedVehicle.assignedDriver || ""
        ).trim();
        const isAssignedToCurrentDriver =
          Boolean(selectedVehicleAssignedDriver) &&
          driverIdentifiers.includes(selectedVehicleAssignedDriver);
        const canAssignVehicle =
          normalizedVehicleId === currentAssignedVehicleId ||
          isAssignedToCurrentDriver ||
          isVehicleReadyForAssignment(selectedVehicle, tasks, {
            lockHours: 4,
          });

        if (!canAssignVehicle) {
          window.alert(
            "This fleet vehicle is currently assigned to another active task."
          );
          return;
        }
      }

      if (currentAssignedVehicleId && currentAssignedVehicleId !== normalizedVehicleId) {
        const previousVehicle = vehicles.find(
          (vehicle) => vehicle.id === currentAssignedVehicleId
        );

        if (
          previousVehicle &&
          driverIdentifiers.includes(
            String(previousVehicle.assignedDriver || "").trim()
          )
        ) {
          await updateVehicle(previousVehicle.id, {
            assignedDriver: null,
          });
        }
      }

      if (selectedVehicle && driverPrimaryIdentifier) {
        await updateVehicle(selectedVehicle.id, {
          assignedDriver: driverPrimaryIdentifier,
        });
      }

      const driverUpdateResult = await updateDriver(driver.id, {
        assignedVehicle: selectedVehicle
          ? {
              id: selectedVehicle.id,
              type: selectedVehicle.vehicleType || selectedVehicle.type || "",
              model: selectedVehicle.model || "",
              plateNumber:
                selectedVehicle.licensePlate || selectedVehicle.plateNumber || "",
              seats: selectedVehicle.capacity || selectedVehicle.seats || "",
            }
          : null,
      });

      if (!driverUpdateResult?.success) {
        window.alert(
          driverUpdateResult?.error || "Failed to assign fleet vehicle to driver."
        );
      }
    } catch (error) {
      window.alert(error?.message || "Failed to assign fleet vehicle.");
    } finally {
      setAssigningVehicleDriverId("");
    }
  };

  const handleAddAvailability = (entity = null, date = null) => {
    if (readOnly) {
      return;
    }

    setEditMode(false);
    const defaultEntity =
      myScheduleOnly && scheduleScope === "staff" ? staffUsers[0] || null : null;
    setSelectedDriver(entity || defaultEntity);
    setSelectedEntityType(scheduleScope);
    setSelectedAvailabilityIndex(null);

    const dateStr = date
      ? format(date, "yyyy-MM-dd")
      : format(new Date(), "yyyy-MM-dd");

    setEventForm({
      isAvailable: true,
      startDate: dateStr,
      endDate: dateStr,
      startTime: "09:00",
      endTime: "17:00",
      isAllDay: false,
      notes: "",
    });
    setOpenDialog(true);
  };

  const handleEditAvailability = (entity, avail) => {
    if (readOnly) {
      return;
    }

    setEditMode(true);
    setSelectedDriver(entity);
    setSelectedEntityType(scheduleScope);

    const index = entity.availabilitySchedule?.findIndex((a) => {
      const aStart = a.startDate?.toDate?.() || new Date(a.startDate);
      const aEnd = a.endDate?.toDate?.() || new Date(a.endDate);
      const availStart =
        avail.startDate?.toDate?.() || new Date(avail.startDate);
      const availEnd = avail.endDate?.toDate?.() || new Date(avail.endDate);

      return (
        aStart.getTime() === availStart.getTime() &&
        aEnd.getTime() === availEnd.getTime() &&
        a.startTime === avail.startTime &&
        a.endTime === avail.endTime
      );
    });

    setSelectedAvailabilityIndex(index !== -1 ? index : null);

    if (!avail.startDate) {
      console.error("Availability missing startDate:", avail);
      return;
    }

    const startDate = avail.startDate?.toDate
      ? avail.startDate.toDate()
      : new Date(avail.startDate);
    const endDate = avail.endDate?.toDate
      ? avail.endDate.toDate()
      : new Date(avail.endDate);

    setEventForm({
      isAvailable: avail.isAvailable !== false,
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
      startTime: avail.startTime || "09:00",
      endTime: avail.endTime || "17:00",
      isAllDay: avail.isAllDay || false,
      notes: avail.notes || "",
    });
    setOpenDialog(true);
  };

  const handleDeleteAvailability = async () => {
    if (readOnly) {
      return;
    }

    if (!selectedDriver || selectedAvailabilityIndex === null) return;

    if (window.confirm("Are you sure you want to remove this availability?")) {
      const currentSchedule = selectedDriver.availabilitySchedule || [];
      const updatedSchedule = currentSchedule.filter(
        (_, idx) => idx !== selectedAvailabilityIndex
      );

      const result = await saveAvailabilityForEntity(
        selectedEntityType,
        selectedDriver.id,
        updatedSchedule
      );

      if (!result?.success) {
        window.alert(result?.error || "Failed to update availability schedule.");
        return;
      }

      setOpenDialog(false);
    }
  };

  const handleSaveAvailability = async () => {
    if (readOnly) {
      return;
    }

    if (!selectedDriver) return;

    const startDateTime = new Date(
      `${eventForm.startDate}T${eventForm.startTime || "00:00"}`
    );
    const endDateTime = new Date(
      `${eventForm.endDate}T${eventForm.endTime || "23:59"}`
    );

    const newAvailability = {
      isAvailable: eventForm.isAvailable,
      startDate: startDateTime,
      endDate: endDateTime,
      startTime: eventForm.isAllDay ? "" : eventForm.startTime,
      endTime: eventForm.isAllDay ? "" : eventForm.endTime,
      isAllDay: eventForm.isAllDay,
      notes: eventForm.notes,
      updatedAt: new Date().toISOString(),
    };

    const currentSchedule = selectedDriver.availabilitySchedule || [];
    let updatedSchedule;

    if (editMode && selectedAvailabilityIndex !== null) {
      // Update existing entry
      updatedSchedule = [...currentSchedule];
      updatedSchedule[selectedAvailabilityIndex] = newAvailability;
    } else {
      // Add new entry
      updatedSchedule = [...currentSchedule, newAvailability];
    }

    const result = await saveAvailabilityForEntity(
      selectedEntityType,
      selectedDriver.id,
      updatedSchedule
    );

    if (!result?.success) {
      window.alert(result?.error || "Failed to save availability schedule.");
      return;
    }

    setOpenDialog(false);
  };

  const renderAvailabilityBlock = (availabilities, entity, date) => {
    if (!availabilities || availabilities.length === 0) {
      if (readOnly) {
        return <Box className="availability-cell-empty" />;
      }

      return (
        <Box
          className="availability-cell-empty"
          onClick={() => handleAddAvailability(entity, date)}
          sx={{
            cursor: "pointer",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          <IconButton size="small" sx={{ opacity: 0.3 }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box className="availability-cell">
        {availabilities.map((avail, idx) => {
          let timeLabel = "All day";
          if (!avail.isAllDay && avail.startTime && avail.endTime) {
            timeLabel = `${avail.startTime} - ${avail.endTime}`;
          }

          return (
            <Box
              key={idx}
              className={`availability-block ${avail.status}`}
              onClick={readOnly ? undefined : () => handleEditAvailability(entity, avail)}
              sx={{
                backgroundColor:
                  avail.status === "available" ? "#5B8DEE" : "#FFB4C8",
                color: avail.status === "available" ? "white" : "#DC143C",
                padding: "6px 10px",
                borderRadius: "6px",
                marginBottom: "6px",
                cursor: readOnly ? "default" : "pointer",
                fontSize: "12px",
                fontWeight: 500,
                ...(readOnly
                  ? {}
                  : {
                      "&:hover": {
                        opacity: 0.85,
                        transform: "translateY(-1px)",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      },
                    }),
                transition: "all 0.2s ease",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, display: "block", fontSize: "11px" }}
              >
                {avail.status === "available" ? "Available" : "Unavailable"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: "10px", opacity: 0.95 }}
              >
                {timeLabel}
              </Typography>
              {avail.notes && avail.notes !== "NAN" && (
                <Typography
                  variant="caption"
                  sx={{ fontSize: "9px", opacity: 0.85, mt: 0.5 }}
                >
                  {avail.notes}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
            {titleOverride || `${getEntityTypeLabel(scheduleScope)} Schedule`}
          </Typography>
          {!hideScopeToggle && (
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              <Button
                variant={scheduleScope === "drivers" ? "contained" : "outlined"}
                onClick={() => {
                  setScheduleScope("drivers");
                  setSelectedDriver(null);
                  setSelectedEntityType("drivers");
                  setOpenDialog(false);
                }}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Drivers
              </Button>
              <Button
                variant={scheduleScope === "staff" ? "contained" : "outlined"}
                onClick={() => {
                  setScheduleScope("staff");
                  setSelectedDriver(null);
                  setSelectedEntityType("staff");
                  setOpenDialog(false);
                }}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Staff
              </Button>
            </Box>
          )}
        </Box>
        {!readOnly && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleAddAvailability()}
            disabled={
              myScheduleOnly && scheduleScope === "staff" && staffUsers.length === 0
            }
            sx={{
              bgcolor: "#5B8DEE",
              "&:hover": { bgcolor: "#4a7dd9" },
              boxShadow: "0 2px 4px rgba(91, 141, 238, 0.3)",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Add {getEntityTypeLabel(scheduleScope)} Availability
          </Button>
        )}
      </Box>

      <Paper
        sx={{
          p: 3,
          mb: 2,
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e8e8e8",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
          <TextField
            placeholder={`Search ${
              scheduleScope === "staff" ? "staff" : "drivers"
            }...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ flex: 1, maxWidth: 300 }}
          />

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton
              onClick={handlePreviousWeek}
              size="small"
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Button
              variant="contained"
              size="small"
              onClick={handleToday}
              sx={{
                bgcolor: "#5B8DEE",
                "&:hover": { bgcolor: "#4a7dd9" },
                boxShadow: "0 2px 4px rgba(91, 141, 238, 0.3)",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {format(weekDays[0], "MMM d")} -{" "}
              {format(weekDays[6], "MMM d, yyyy")}
            </Button>
            <IconButton
              onClick={handleNextWeek}
              size="small"
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Schedule Grid */}
        <Box className="schedule-container">
          {/* Header Row */}
          <Box className="schedule-header">
            <Box className="driver-column-header">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {getEntityTypeLabel(scheduleScope)}
              </Typography>
            </Box>
            {weekDays.map((day, idx) => (
              <Box key={idx} className="day-column-header">
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, display: "block" }}
                >
                  {format(day, "EEE")}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {format(day, "M/d")}
                </Typography>
              </Box>
            ))}
          </Box>

          {filteredEntities.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No {scheduleScope === "staff" ? "staff users" : "drivers"} found
              </Typography>
            </Box>
          ) : (
            filteredEntities.map((entity) => {
              const ownVehicleLabel =
                scheduleScope === "drivers" ? getOwnVehicleLabel(entity) : "";
              const assignedFleetVehicle =
                scheduleScope === "drivers"
                  ? getAssignedFleetVehicleForDriver(entity)
                  : null;
              const assignedFleetVehicleLabel = getVehicleDisplayName(
                assignedFleetVehicle
              );
              const fleetOptions =
                scheduleScope === "drivers"
                  ? getFleetOptionsForDriver(entity)
                  : [];
              const selectedFleetVehicleId =
                assignedFleetVehicle?.id ||
                entity?.assignedVehicle?.id ||
                entity?.assignedVehicleId ||
                "";
              const isInHouseDriver =
                scheduleScope === "drivers" && !ownVehicleLabel;

              return (
              <Box key={entity.id} className="schedule-row">
                <Box className="driver-column">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Box
                      onClick={() => handleOpenEntityDetails(entity)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        width: "100%",
                      }}
                    >
                      <Avatar 
                        src={
                          scheduleScope === "drivers"
                            ? entity.profilePictureUrl || ""
                            : entity.photoURL || ""
                        }
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          bgcolor: "#f5f5f5",
                          border: '2px solid #e0e0e0'
                        }}
                      >
                        {entity.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {entity.name}
                        </Typography>
                        {scheduleScope !== "drivers" && (
                          <Typography variant="caption" color="text.secondary">
                            {getEntitySubtitle(entity, scheduleScope)}
                          </Typography>
                        )}
                        {scheduleScope === "drivers" && ownVehicleLabel && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#1976d2", display: "block" }}
                          >
                            Own Vehicle: {ownVehicleLabel}
                          </Typography>
                        )}
                        {scheduleScope === "drivers" &&
                          !ownVehicleLabel &&
                          assignedFleetVehicleLabel && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#2e7d32", display: "block" }}
                            >
                              Fleet: {assignedFleetVehicleLabel}
                            </Typography>
                          )}
                      </Box>
                    </Box>

                    {canAssignFleetVehicle && isInHouseDriver && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Fleet Vehicle</InputLabel>
                        <Select
                          value={selectedFleetVehicleId}
                          label="Fleet Vehicle"
                          disabled={assigningVehicleDriverId === entity.id}
                          onChange={(event) => {
                            handleAssignFleetVehicleToDriver(
                              entity,
                              event.target.value
                            );
                          }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {fleetOptions.map((vehicle) => (
                            <MenuItem key={vehicle.id} value={vehicle.id}>
                              {`${getVehicleDisplayName(vehicle)} [${formatStatusLabel(
                                getVehicleEffectiveStatus(vehicle, tasks, {
                                  lockHours: 4,
                                }),
                                "Available"
                              )}]`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Box>
                {weekDays.map((day, idx) => (
                  <Box key={idx} className="day-column">
                    {renderAvailabilityBlock(
                      getEntityAvailability(entity, day),
                      entity,
                      day
                    )}
                  </Box>
                ))}
              </Box>
            );
            })
          )}
        </Box>
      </Paper>

      <DriverDetailsModal
        open={openDriverDetailsModal}
        onClose={handleCloseDriverDetails}
        driver={selectedDriverForDetails}
      />

      <StaffDetailsModal
        open={openStaffDetailsModal}
        onClose={handleCloseStaffDetails}
        staff={selectedStaffForDetails}
      />

      {/* Add/Edit Availability Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editMode
            ? "Edit Availability"
            : `Add ${getEntityTypeLabel(scheduleScope)} Availability`}
          {selectedDriver && (
            <Typography variant="body2" color="text.secondary">
              {selectedDriver.name} ({getEntitySubtitle(selectedDriver, selectedEntityType)})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {!selectedDriver && (
              <FormControl fullWidth>
                <InputLabel>Select {getEntityTypeLabel(scheduleScope)}</InputLabel>
                <Select
                  value={selectedDriver?.id || ""}
                  label={`Select ${getEntityTypeLabel(scheduleScope)}`}
                  onChange={(e) => {
                    const entity = activeEntities.find(
                      (item) => item.id === e.target.value
                    );
                    setSelectedDriver(entity || null);
                    setSelectedEntityType(scheduleScope);
                  }}
                >
                  {activeEntities.map((entity) => (
                    <MenuItem key={entity.id} value={entity.id}>
                      {entity.name} - {getEntitySubtitle(entity, scheduleScope)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={eventForm.isAvailable ? "available" : "unavailable"}
                label="Status"
                onChange={(e) =>
                  setEventForm({
                    ...eventForm,
                    isAvailable: e.target.value === "available",
                  })
                }
              >
                <MenuItem value="available">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        bgcolor: "#4CAF50",
                        borderRadius: 1,
                      }}
                    />
                    Available
                  </Box>
                </MenuItem>
                <MenuItem value="unavailable">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        bgcolor: "#f44336",
                        borderRadius: 1,
                      }}
                    />
                    Unavailable
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={eventForm.startDate}
                onChange={(e) =>
                  setEventForm({ ...eventForm, startDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={eventForm.endDate}
                onChange={(e) =>
                  setEventForm({ ...eventForm, endDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                helperText="Leave same as start for single day"
              />
            </Box>

            <FormControl fullWidth>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  type="checkbox"
                  checked={eventForm.isAllDay}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, isAllDay: e.target.checked })
                  }
                  style={{ width: 18, height: 18 }}
                />
                <Typography variant="body2">All Day</Typography>
              </Box>
            </FormControl>

            {!eventForm.isAllDay && (
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={eventForm.startTime}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, startTime: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={eventForm.endTime}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, endTime: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={eventForm.notes}
              onChange={(e) =>
                setEventForm({ ...eventForm, notes: e.target.value })
              }
              placeholder="Add any notes about this availability..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {editMode && selectedDriver && (
            <Button
              onClick={handleDeleteAvailability}
              variant="contained"
              startIcon={<DeleteIcon />}
              sx={{
                bgcolor: "#f44336",
                color: "white",
                "&:hover": { bgcolor: "#d32f2f" },
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Delete
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => setOpenDialog(false)}
            variant="contained"
            sx={{
              bgcolor: "#9e9e9e",
              color: "white",
              "&:hover": {
                bgcolor: "#757575",
              },
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAvailability}
            variant="contained"
            disabled={!selectedDriver}
            sx={{
              bgcolor: editMode ? "#FF9800" : "#4CAF50",
              color: "white",
              "&:hover": {
                bgcolor: editMode ? "#F57C00" : "#388E3C",
              },
              "&:disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e",
              },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {editMode ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverSchedule;
